import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../firebase';
import { useComisionLedger } from '../../hooks/useComisionLedger';
import { useAsociados, useClients, useContracts } from '../../contexts/DataContext';
import { FianzaView } from '../../components/FianzaView';
import { useUser } from '../../components/UserContext';
import { Heading } from '../../components/heading';
import { Text } from '../../components/text';
import { Button } from '../../components/button';
import { TipoAsociadoChip } from '../../components/RepartoEditor';
import { comisionPorAsociado, comisionPorAsociadoAnual, candidatosReparto, periodoDe } from '../../utils/reparto';
import { marcarRepartoPago } from '../../api/comisiones';
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

const MESES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];
const MESES_CORTOS = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

const TIPO_MOV = { emision: 'Emisión', adicional: 'Adicional', cargo: 'Cargo', sustituye: 'Sustitución' };

const fmtMXN = (n) =>
  new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(Number(n) || 0);

const fmtFecha = (ms) =>
  ms ? new Date(Number(ms)).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const hoyISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/**
 * ComisionPorAsociado — each asociado's and socio's cut of the month's
 * comisión, from the reparto[] snapshots on the ledger. Owner-only and only
 * with orgs.asociadosEnabled (gated in Reportes.jsx).
 *
 * Asociados are paid: the row carries the month's pago (frozen monto at
 * marking time) and flags any later restatement as an ajuste. Socios are
 * report-only. Movements booked without reparto sit in "Sin reparto" so the
 * total squares with Liquidación for the same month.
 */
export function ComisionPorAsociado({ selectedYear }) {
  const { userData } = useUser();
  const { movements, loading } = useComisionLedger();
  const { asociadosById } = useAsociados();
  const ahora = new Date();
  const [mes, setMes] = useState(
    selectedYear === ahora.getFullYear() ? ahora.getMonth() : 11
  );
  const [vista, setVista] = useState('mes'); // mes | anio
  const [pagosAnio, setPagosAnio] = useState({}); // asociadoId -> { 'YYYY-MM': pago }
  const [abierto, setAbierto] = useState(null);
  const [pagos, setPagos] = useState({});
  const [pagando, setPagando] = useState(null); // row being marked
  const [error, setError] = useState(null);
  const { contractsById, contractsList } = useContracts();
  const { clientsById } = useClients();
  const navigate = useNavigate();
  const [fianzaAbierta, setFianzaAbierta] = useState(null);

  // Same shape ComisionPorConvenio hands FianzaView: the live fianza off its
  // contract, not the movement snapshot.
  const abrirFianza = (m) => {
    const contrato = contractsById?.[m.contratoId];
    const fianza = (contrato?.fianzas || []).find((f) => f.id === m.fianzaId);
    if (!contrato || !fianza) {
      setError('La fianza de este movimiento ya no está en su contrato.');
      return;
    }
    setFianzaAbierta({ ...fianza, contratoId: contrato.id, _contrato: contrato });
  };

  useEffect(() => {
    setMes(selectedYear === new Date().getFullYear() ? new Date().getMonth() : 11);
  }, [selectedYear]);

  const periodo = periodoDe(selectedYear, mes);
  const { rows, sinReparto, total } = useMemo(
    () => comisionPorAsociado(movements, selectedYear, mes, asociadosById),
    [movements, selectedYear, mes, asociadosById]
  );
  const anual = useMemo(
    () => comisionPorAsociadoAnual(movements, selectedYear, asociadosById),
    [movements, selectedYear, asociadosById]
  );

  // Pagos of the whole year for the asociado rows of the annual view (one
  // query per asociado; a socio is never paid).
  const idsAnual = anual.rows.filter((r) => r.tipo !== 'socio').map((r) => r.asociadoId).join(',');
  useEffect(() => {
    if (vista !== 'anio' || !userData?.org || !idsAnual) { setPagosAnio({}); return; }
    let vivo = true;
    (async () => {
      const out = {};
      await Promise.all(idsAnual.split(',').map(async (id) => {
        try {
          const snap = await getDocs(query(
            collection(db, 'orgs', userData.org, 'asociados', id, 'pagos'),
            where('periodo', '>=', `${selectedYear}-01`),
            where('periodo', '<=', `${selectedYear}-12`),
          ));
          out[id] = {};
          snap.forEach((d) => { out[id][d.id] = d.data(); });
        } catch (e) {
          console.error('pagos del año read failed', id, e);
        }
      }));
      if (vivo) setPagosAnio(out);
    })();
    return () => { vivo = false; };
  }, [vista, userData?.org, idsAnual, selectedYear]);

  const irAlMes = (m) => { setMes(m); setVista('mes'); setAbierto(null); };

  // Fianzas with a movement this month whose booked cut differs from what
  // their cliente resolves today: money the table attributes under a reparto
  // the owner has since changed. Each row leads to the cliente's Asociados
  // tab, where the retroactive dialog restates them.
  const anteriores = useMemo(() => {
    const enMes = new Set();
    [...rows.flatMap((r) => r.movimientos), ...sinReparto.movimientos].forEach((m) => {
      if (m.fianzaId) enMes.add(m.fianzaId);
    });
    if (!enMes.size) return [];
    return candidatosReparto({
      movements,
      contratos: contractsList || [],
      clientesById: clientsById || {},
      asociadosById,
    }).filter((c) => enMes.has(c.fianzaId));
  }, [rows, sinReparto, movements, contractsList, clientsById, asociadosById]);

  const idsAsociados = rows.filter((r) => r.tipo !== 'socio').map((r) => r.asociadoId).join(',');
  const cargarPagos = useCallback(async () => {
    if (!userData?.org || !idsAsociados) { setPagos({}); return; }
    const out = {};
    await Promise.all(idsAsociados.split(',').map(async (id) => {
      try {
        const snap = await getDoc(doc(db, 'orgs', userData.org, 'asociados', id, 'pagos', periodo));
        if (snap.exists()) out[id] = snap.data();
      } catch (e) {
        console.error('pago read failed', id, e);
      }
    }));
    setPagos(out);
  }, [userData?.org, idsAsociados, periodo]);

  useEffect(() => { cargarPagos(); }, [cargarPagos]);

  const desmarcar = async (r) => {
    setError(null);
    try {
      await marcarRepartoPago({ asociadoId: r.asociadoId, periodo, pagado: false });
      await cargarPagos();
    } catch (e) {
      setError(e.message || 'No se pudo actualizar el pago.');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-azul-surelia"></div>
      </div>
    );
  }

  const porPagar = rows
    .filter((r) => r.tipo !== 'socio' && !pagos[r.asociadoId]?.pagado)
    .reduce((s, r) => s + r.monto, 0);

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <Heading level={2} className="text-xl text-azul-surelia">Comisión por asociado</Heading>
          <Text className="text-sm text-azul-surelia/70">
            {vista === 'anio' ? `Año ${selectedYear}` : `${MESES[mes]} ${selectedYear}`}
          </Text>
        </div>
        <div className="flex items-center gap-2" data-pdf-hide>
          <div className="inline-flex rounded-lg border border-azul-surelia/20 overflow-hidden">
            {[['mes', 'Mes'], ['anio', 'Año']].map(([v, label]) => (
              <button
                key={v}
                type="button"
                onClick={() => { setVista(v); setAbierto(null); }}
                className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                  vista === v ? 'bg-azul-surelia text-white' : 'bg-white text-azul-surelia hover:bg-zinc-50'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          {vista === 'mes' && (
            <select
              value={mes}
              onChange={(e) => { setMes(Number(e.target.value)); setAbierto(null); }}
              className="rounded-lg border border-azul-surelia/20 px-3 py-1.5 text-sm text-azul-surelia"
            >
              {MESES.map((m, i) => <option key={m} value={i}>{m}</option>)}
            </select>
          )}
        </div>
      </div>

      {vista === 'anio' && (
        <VistaAnual
          anual={anual}
          anio={selectedYear}
          pagos={pagosAnio}
          onMes={irAlMes}
        />
      )}

      {vista === 'mes' && (<>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="rounded-xl border border-gray-200 bg-zinc-50 p-3">
          <div className="text-[10px] uppercase tracking-wide text-gray-500">Comisión del mes</div>
          <div className="text-lg font-bold text-azul-surelia">{fmtMXN(total)}</div>
        </div>
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-3">
          <div className="text-[10px] uppercase tracking-wide text-amber-600">Por pagar a asociados</div>
          <div className="text-lg font-bold text-amber-800">{fmtMXN(porPagar)}</div>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-3">
          <div className="text-[10px] uppercase tracking-wide text-gray-500">Sin reparto</div>
          <div className="text-lg font-bold text-gray-700">{fmtMXN(sinReparto.monto)}</div>
        </div>
        <div className={`rounded-xl border p-3 ${anteriores.length ? 'border-amber-200 bg-amber-50' : 'border-gray-200 bg-white'}`}>
          <div className={`text-[10px] uppercase tracking-wide ${anteriores.length ? 'text-amber-600' : 'text-gray-500'}`}>
            Con reparto anterior
          </div>
          <div className={`text-lg font-bold ${anteriores.length ? 'text-amber-800' : 'text-gray-700'}`}>
            {anteriores.length} fianza{anteriores.length !== 1 ? 's' : ''}
          </div>
        </div>
      </div>

      {anteriores.length > 0 && (
        <div className="rounded-xl border border-amber-200 bg-white overflow-hidden">
          <div className="px-4 py-2 text-xs font-medium text-amber-900 bg-amber-50 border-b border-amber-200">
            Fianzas del mes con un reparto distinto al vigente de su cliente
          </div>
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-gray-400">
                <th className="py-1.5 px-4 font-medium">Emisión</th>
                <th className="py-1.5 px-3 font-medium">Fianza</th>
                <th className="py-1.5 px-3 font-medium">Cliente</th>
                <th className="py-1.5 px-3 font-medium text-right">Comisión</th>
                <th className="py-1.5 px-3 font-medium" />
              </tr>
            </thead>
            <tbody>
              {anteriores.map((c) => (
                <tr key={c.fianzaId} className="border-t border-gray-100">
                  <td className="py-1.5 px-4 text-gray-500 whitespace-nowrap">{fmtFecha(c.fecha)}</td>
                  <td className="py-1.5 px-3 font-medium text-azul-surelia">{c.numeroFianza || 'Sin número'}</td>
                  <td className="py-1.5 px-3 text-gray-600 truncate max-w-[16rem]">{c.clienteNombre || '—'}</td>
                  <td className="py-1.5 px-3 text-right tabular-nums text-gray-600">{fmtMXN(c.comision)}</td>
                  <td className="py-1.5 px-3 text-right" data-pdf-hide>
                    {c.clienteId && (
                      <button
                        type="button"
                        className="font-medium text-azul-surelia hover:underline"
                        onClick={() => navigate(`/app/clientes/${c.clienteId}?tab=asociados`)}
                      >
                        Revisar
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-700">{error}</div>
      )}

      {rows.length === 0 && sinReparto.movimientos.length === 0 ? (
        <div className="rounded-xl border border-gray-200 bg-white p-6 text-center text-sm text-gray-500">
          No hay movimientos de comisión en {MESES[mes].toLowerCase()} {selectedYear}.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-azul-surelia text-left text-xs font-medium text-white">
                <th className="py-2 pl-2 pr-3">Asociado</th>
                <th className="py-2 px-3">Tipo</th>
                <th className="py-2 px-3 text-right">Fianzas</th>
                <th className="py-2 px-3 text-right">Comisión</th>
                <th className="py-2 px-3">Pago</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const pago = pagos[r.asociadoId];
                const ajuste = pago?.pagado ? Math.round((r.monto - (pago.monto || 0)) * 100) / 100 : 0;
                return (
                  <React.Fragment key={r.asociadoId}>
                    <tr
                      className="border-b border-gray-100 cursor-pointer hover:bg-zinc-50"
                      onClick={() => setAbierto(abierto === r.asociadoId ? null : r.asociadoId)}
                    >
                      <td className="py-2 pl-2 pr-3 font-medium text-gray-800">
                        <span className="inline-flex items-center gap-1">
                          {abierto === r.asociadoId
                            ? <ChevronDownIcon className="w-3.5 h-3.5 text-gray-400" />
                            : <ChevronRightIcon className="w-3.5 h-3.5 text-gray-400" />}
                          {r.nombre}
                        </span>
                      </td>
                      <td className="py-2 px-3"><TipoAsociadoChip tipo={r.tipo} /></td>
                      <td className="py-2 px-3 text-right text-gray-500">{r.fianzas}</td>
                      <td className={`py-2 px-3 text-right font-semibold tabular-nums ${r.monto < 0 ? 'text-red-600' : 'text-azul-surelia'}`}>
                        {fmtMXN(r.monto)}
                      </td>
                      <td className="py-2 px-3" onClick={(e) => e.stopPropagation()}>
                        {r.tipo === 'socio' ? (
                          <span className="text-xs text-gray-400">Socio</span>
                        ) : pago?.pagado ? (
                          <div className="flex flex-wrap items-center gap-2 text-xs">
                            <span className="inline-flex rounded-full bg-green-100 px-2 py-0.5 font-medium text-green-800">
                              Pagado {fmtMXN(pago.monto)}
                            </span>
                            {Math.abs(ajuste) >= 0.01 && (
                              <span className="inline-flex rounded-full bg-amber-100 px-2 py-0.5 font-medium text-amber-800"
                                title="El monto del mes cambió después de marcar el pago.">
                                Ajuste {fmtMXN(ajuste)}
                              </span>
                            )}
                            <button type="button" className="text-gray-400 hover:text-gray-600" onClick={() => desmarcar(r)}>
                              Desmarcar
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2 text-xs">
                            <span className="inline-flex rounded-full bg-amber-100 px-2 py-0.5 font-medium text-amber-800">Pendiente</span>
                            {r.monto > 0 && (
                              <button type="button" className="font-medium text-azul-surelia hover:underline" onClick={() => setPagando(r)}>
                                Marcar pagado
                              </button>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                    {abierto === r.asociadoId && (
                      <tr className="bg-zinc-50/60">
                        <td colSpan={5} className="px-6 py-2">
                          <DetalleMovimientos movimientos={r.movimientos} onAbrir={abrirFianza} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
              {sinReparto.movimientos.length > 0 && (
                <React.Fragment>
                  <tr
                    className="border-b border-gray-100 cursor-pointer hover:bg-zinc-50"
                    onClick={() => setAbierto(abierto === '__sin' ? null : '__sin')}
                  >
                    <td className="py-2 pl-2 pr-3 text-gray-500">
                      <span className="inline-flex items-center gap-1">
                        {abierto === '__sin'
                          ? <ChevronDownIcon className="w-3.5 h-3.5 text-gray-400" />
                          : <ChevronRightIcon className="w-3.5 h-3.5 text-gray-400" />}
                        Sin reparto
                      </span>
                    </td>
                    <td className="py-2 px-3" />
                    <td className="py-2 px-3 text-right text-gray-500">
                      {new Set(sinReparto.movimientos.map((m) => m.fianzaId)).size}
                    </td>
                    <td className="py-2 px-3 text-right tabular-nums text-gray-600">{fmtMXN(sinReparto.monto)}</td>
                    <td className="py-2 px-3" />
                  </tr>
                  {abierto === '__sin' && (
                    <tr className="bg-zinc-50/60">
                      <td colSpan={5} className="px-6 py-2">
                        <DetalleMovimientos movimientos={sinReparto.movimientos} sinParte onAbrir={abrirFianza} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              )}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-gray-300 font-semibold text-gray-800">
                <td className="py-2 pl-2 pr-3" colSpan={3}>Total</td>
                <td className="py-2 px-3 text-right tabular-nums">{fmtMXN(total)}</td>
                <td />
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      </>)}

      {fianzaAbierta &&
        createPortal(
          <FianzaView
            isOpen={!!fianzaAbierta}
            onClose={() => setFianzaAbierta(null)}
            fianza={fianzaAbierta}
            clientData={{}}
            contratoData={fianzaAbierta._contrato}
            onUpdate={() => {}}
          />,
          document.body
        )}

      <PagoDialog
        row={pagando}
        periodo={periodo}
        etiqueta={`${MESES[mes]} ${selectedYear}`}
        onClose={() => setPagando(null)}
        onSaved={async () => { setPagando(null); await cargarPagos(); }}
      />
    </div>
  );
}

/**
 * The year as a matrix: one row per asociado, a column per month, totals
 * on both axes. An asociado's cell turns green once that month is marked
 * pagado (a socio is never paid, so its cells stay neutral). Clicking a
 * month header or cell opens that month.
 */
function VistaAnual({ anual, anio, pagos, onMes }) {
  const { rows, sinReparto, totalesMes, total } = anual;
  const vacio = rows.length === 0 && sinReparto.total === 0;
  const porPagar = rows
    .filter((r) => r.tipo !== 'socio')
    .reduce((s, r) => s + r.meses.reduce(
      (t, m, i) => t + (m > 0 && !pagos[r.asociadoId]?.[periodoDe(anio, i)]?.pagado ? m : 0), 0), 0);

  const celda = (monto, cls = '') => (
    <span className={`tabular-nums ${monto === 0 ? 'text-gray-300' : cls}`}>
      {monto === 0 ? '—' : fmtMXN(monto)}
    </span>
  );

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <div className="rounded-xl border border-gray-200 bg-zinc-50 p-3">
          <div className="text-[10px] uppercase tracking-wide text-gray-500">Comisión del año</div>
          <div className="text-lg font-bold text-azul-surelia">{fmtMXN(total)}</div>
        </div>
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-3">
          <div className="text-[10px] uppercase tracking-wide text-amber-600">Por pagar a asociados</div>
          <div className="text-lg font-bold text-amber-800">{fmtMXN(porPagar)}</div>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-3">
          <div className="text-[10px] uppercase tracking-wide text-gray-500">Sin reparto</div>
          <div className="text-lg font-bold text-gray-700">{fmtMXN(sinReparto.total)}</div>
        </div>
      </div>

      {vacio ? (
        <div className="rounded-xl border border-gray-200 bg-white p-6 text-center text-sm text-gray-500">
          No hay movimientos de comisión en {anio}.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-azul-surelia text-left font-medium text-white">
                <th className="py-2 pl-2 pr-3 sticky left-0 bg-azul-surelia">Asociado</th>
                {MESES_CORTOS.map((m, i) => (
                  <th key={m} className="py-2 px-2 text-right">
                    <button type="button" onClick={() => onMes(i)} className="hover:underline">{m}</button>
                  </th>
                ))}
                <th className="py-2 px-3 text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.asociadoId} className="border-b border-gray-100 hover:bg-zinc-50">
                  <td className="py-2 pl-2 pr-3 sticky left-0 bg-white whitespace-nowrap">
                    <span className="font-medium text-gray-800">{r.nombre}</span>
                    <span className="ml-2"><TipoAsociadoChip tipo={r.tipo} /></span>
                  </td>
                  {r.meses.map((m, i) => {
                    const pagado = r.tipo !== 'socio' && pagos[r.asociadoId]?.[periodoDe(anio, i)]?.pagado;
                    return (
                      <td
                        key={i}
                        className="py-2 px-2 text-right cursor-pointer"
                        onClick={() => onMes(i)}
                        title={pagado ? 'Pagado' : undefined}
                      >
                        {celda(m, pagado ? 'text-emerald-700 font-medium' : m < 0 ? 'text-red-600' : 'text-gray-700')}
                      </td>
                    );
                  })}
                  <td className={`py-2 px-3 text-right font-semibold tabular-nums ${r.total < 0 ? 'text-red-600' : 'text-azul-surelia'}`}>
                    {fmtMXN(r.total)}
                  </td>
                </tr>
              ))}
              {sinReparto.total !== 0 && (
                <tr className="border-b border-gray-100 text-gray-500">
                  <td className="py-2 pl-2 pr-3 sticky left-0 bg-white">Sin reparto</td>
                  {sinReparto.meses.map((m, i) => (
                    <td key={i} className="py-2 px-2 text-right cursor-pointer" onClick={() => onMes(i)}>
                      {celda(m, 'text-gray-600')}
                    </td>
                  ))}
                  <td className="py-2 px-3 text-right tabular-nums">{fmtMXN(sinReparto.total)}</td>
                </tr>
              )}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-gray-300 font-semibold text-gray-800">
                <td className="py-2 pl-2 pr-3 sticky left-0 bg-white">Total</td>
                {totalesMes.map((m, i) => (
                  <td key={i} className="py-2 px-2 text-right tabular-nums">{m === 0 ? '—' : fmtMXN(m)}</td>
                ))}
                <td className="py-2 px-3 text-right tabular-nums">{fmtMXN(total)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}

function DetalleMovimientos({ movimientos, sinParte = false, onAbrir }) {
  return (
    <table className="w-full text-xs">
      <thead>
        <tr className="text-left text-gray-400">
          <th className="py-1 pr-3 font-medium">Fecha</th>
          <th className="py-1 pr-3 font-medium">Fianza</th>
          <th className="py-1 pr-3 font-medium">Cliente</th>
          <th className="py-1 pr-3 font-medium">Tipo</th>
          <th className="py-1 pr-3 font-medium text-right">Comisión</th>
          {!sinParte && <th className="py-1 font-medium text-right">Parte</th>}
        </tr>
      </thead>
      <tbody>
        {movimientos
          .slice()
          .sort((a, b) => (Number(a.fecha) || 0) - (Number(b.fecha) || 0))
          .map((m) => (
            <tr
              key={m.id}
              className="border-t border-gray-100 cursor-pointer hover:bg-white"
              onClick={() => onAbrir?.(m)}
            >
              <td className="py-1 pr-3 text-gray-500">{fmtFecha(m.fecha)}</td>
              <td className="py-1 pr-3 font-medium text-azul-surelia">{m.numeroFianza || 'Sin número'}</td>
              <td className="py-1 pr-3 text-gray-500 truncate max-w-[14rem]">{m.clienteNombre || m.convenioNombre || '—'}</td>
              <td className="py-1 pr-3 text-gray-500">{TIPO_MOV[m.tipo] || m.tipo}</td>
              <td className="py-1 pr-3 text-right tabular-nums text-gray-600">{fmtMXN(m.comisionMonto)}</td>
              {!sinParte && (
                <td className="py-1 text-right tabular-nums font-medium text-gray-800">
                  {fmtMXN(m.parte)}
                  {m.partePct != null && <span className="ml-1 text-gray-400">({m.partePct}%)</span>}
                </td>
              )}
            </tr>
          ))}
      </tbody>
    </table>
  );
}

function PagoDialog({ row, periodo, etiqueta, onClose, onSaved }) {
  const [fecha, setFecha] = useState(hoyISO());
  const [nota, setNota] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (row) { setFecha(hoyISO()); setNota(''); setError(null); }
  }, [row]);

  if (!row) return null;

  const guardar = async () => {
    const [y, m, d] = fecha.split('-').map(Number);
    if (!y || !m || !d) { setError('La fecha no es válida.'); return; }
    setSaving(true);
    setError(null);
    try {
      await marcarRepartoPago({
        asociadoId: row.asociadoId,
        periodo,
        pagado: true,
        nota: nota.trim() || null,
        pagadoFecha: new Date(y, m - 1, d, 12).getTime(),
      });
      await onSaved();
    } catch (e) {
      setError(e.message || 'No se pudo registrar el pago.');
    } finally {
      setSaving(false);
    }
  };

  const field =
    'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none ' +
    'focus:border-azul-surelia focus:ring-1 focus:ring-azul-surelia';

  return createPortal(
    <div className="fixed inset-0 z-[70] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={() => !saving && onClose()} />
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
        <div className="px-5 py-4 border-b border-zinc-100">
          <h3 className="text-base font-semibold text-azul-surelia">Pago a {row.nombre}</h3>
          <p className="text-xs text-zinc-500 mt-0.5">{etiqueta}</p>
        </div>
        <div className="px-5 py-4 space-y-3">
          {error && (
            <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-700">{error}</div>
          )}
          <div className="rounded-xl border border-azul-surelia/20 bg-azul-surelia/5 px-4 py-3 flex items-center justify-between">
            <span className="text-sm text-zinc-600">Monto</span>
            <span className="text-lg font-bold text-azul-surelia tabular-nums">{fmtMXN(row.monto)}</span>
          </div>
          <label className="block">
            <span className="block text-xs font-medium text-zinc-600 mb-1">Fecha de pago</span>
            <input type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} className={field} />
          </label>
          <label className="block">
            <span className="block text-xs font-medium text-zinc-600 mb-1">Nota</span>
            <input type="text" value={nota} onChange={(e) => setNota(e.target.value)} className={field}
              onKeyDown={(e) => { if (e.key === 'Escape') onClose(); }} />
          </label>
        </div>
        <div className="px-5 py-3 bg-zinc-50 border-t border-zinc-100 flex justify-end gap-2">
          <Button plain onClick={onClose} disabled={saving}>Cancelar</Button>
          <Button color="surelia" onClick={guardar} disabled={saving}>
            {saving ? 'Guardando…' : 'Marcar pagado'}
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default ComisionPorAsociado;
